import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import { Logo } from "@/components/Logo";
import { ArrowRight, Sparkles, IndianRupee, TrendingUp, LayoutGrid, Link2, CalendarCheck, ShieldCheck, Zap, Check, Radio, BadgeCheck } from "lucide-react";

const HERO_IMG = "https://images.unsplash.com/photo-1582610116397-edb318620f90?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400";
const CHANNELS_A = ["Airbnb", "Booking.com", "Agoda", "Expedia", "MakeMyTrip", "Vrbo", "Trip.com", "Hostelworld", "Goibibo", "EaseMyTrip", "Cleartrip", "Yatra", "Hotels.com", "Traveloka", "Despegar"];
const CHANNELS_B = ["Hopper", "Tiket.com", "Ctrip", "Wego", "Kayak", "HomeToGo", "Holidu", "Trivago", "StayVista", "SaffronStays", "Plum Guide", "Marriott Homes", "Google Hotels", "HRS", "Ostrovok"];

const STEPS = [
  { icon: LayoutGrid, t: "Create property & units", d: "Add as many units as the property has — 10, 20, 40. Rename each one; your name is its primary identity everywhere." },
  { icon: Link2, t: "Connect with AI threads", d: "Our AI pre-draws unit connections and listing matches as dashed threads. You just tap to confirm." },
  { icon: IndianRupee, t: "Pay per property, activate instantly", d: "₹475/unit — the same rate as the OTA. The moment payment clears, that property's PMS is live." },
  { icon: Zap, t: "Distribute to 150+ channels", d: "Paste all your Booking.com or Airbnb IDs at once. One pull, one confirm, and you're everywhere." },
];

const MiniThread = () => (
  <svg viewBox="0 0 220 120" className="w-full h-28">
    <path d="M 30,25 C 110,25 110,60 190,60" fill="none" stroke="#4F46E5" strokeWidth="2.5" strokeLinecap="round" />
    <path d="M 30,60 C 110,60 110,95 190,95" fill="none" stroke="#4F46E5" strokeWidth="2.5" strokeDasharray="6 5" className="thread-dashed" strokeLinecap="round" />
    <path d="M 30,95 C 110,95 110,25 190,25" fill="none" stroke="#0EA5E9" strokeWidth="2.5" strokeLinecap="round" />
    {[25, 60, 95].map((y) => <g key={y}><circle cx="30" cy={y} r="5" fill="#0F172A" /><circle cx="190" cy={y} r="5" fill="#CBD5E1" /></g>)}
    <g><rect x="92" y="68" rx="9" width="52" height="18" fill="#fff" stroke="#4F46E5" /><text x="118" y="81" textAnchor="middle" fontSize="10" fontWeight="700" fill="#4F46E5">AI ✓</text></g>
  </svg>
);

export default function MarketingPage() {
  const nav = useNavigate();
  const { token } = useAuth();
  const [unitsN, setUnitsN] = useState(8);
  const [adr, setAdr] = useState(3500);

  const annualRevenue = unitsN * adr * 365 * 0.6;
  const otaCommission = annualRevenue * 0.18;
  const atitiCost = unitsN * 475 * 12;
  const saved = otaCommission - atitiCost;
  const fmt = (n) => "₹" + Math.round(n).toLocaleString("en-IN");
  const cta = () => nav(token ? "/pms" : "/login");

  return (
    <div className="bg-white text-slate-900">
      <header className="backdrop-blur-xl bg-white/90 border-b border-slate-200/80 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 sm:h-20 flex items-center justify-between gap-3">
          <a href="/" data-testid="header-logo"><Logo h={40} tag="PMS" /></a>
          <div className="flex items-center gap-2 sm:gap-3">
            <a href="#demo" data-testid="header-book-demo-pill"
              className="rounded-full pl-4 pr-4 sm:pl-4 sm:pr-5 py-2 sm:py-2.5 text-xs sm:text-sm font-semibold flex items-center gap-1.5 sm:gap-2 text-white shadow-md shadow-pink-500/20 transition-transform hover:scale-[1.03]"
              style={{ background: "linear-gradient(135deg,#EC4899 0%,#DB2777 60%,#BE185D 100%)" }}
              title="Book a 30-minute live demo">
              <CalendarCheck size={14} /> <span className="hidden xs:inline">Book demo</span><span className="inline xs:hidden">Demo</span>
            </a>
            <button data-testid="header-cta-button" onClick={cta}
              className="btn-brand rounded-full px-4 sm:px-5 py-2 sm:py-2.5 text-xs sm:text-sm font-semibold flex items-center gap-1.5 sm:gap-2">
              {token ? "Open my PMS" : "Set up my PMS"} <ArrowRight size={15} />
            </button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 sm:pt-20 pb-10 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7 reveal">
            <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full bg-[#EEF2FF] brand-pink"><Sparkles size={12} /> Atiti PMS — by the zero-commission homestay OTA</span>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-5 leading-[1.04]">
              Know exactly how <span className="brand-pink">each property</span> performs.
            </h1>
            <p className="text-base sm:text-lg text-slate-600 mt-5 max-w-xl leading-relaxed">
              Most PMS tools lump your portfolio into one blur. Atiti PMS is <b>property-wise</b> — revenue, expenses, profit and occupancy for every property, separately. One subscription. One login. 150+ channels.
            </p>
            <div className="flex flex-wrap items-center gap-4 mt-8">
              <button data-testid="hero-signup-button" onClick={cta} className="btn-brand rounded-full px-7 py-3.5 text-sm font-semibold flex items-center gap-2">
                {token ? "Open my PMS" : "Set up my PMS"} <ArrowRight size={16} />
              </button>
              <a href="#demo" data-testid="hero-demo-link" className="text-sm font-semibold text-slate-700 hover:brand-pink flex items-center gap-2 border border-slate-300 rounded-full px-6 py-3.5 bg-white"><CalendarCheck size={16} /> Book a live demo</a>
            </div>
            <div className="flex flex-wrap items-center gap-x-8 gap-y-3 mt-10">
              {[["150+", "OTA channels, one subscription"], ["0%", "host commission on Atiti"], ["₹475", "per unit / month, all-in"]].map(([n, l]) => (
                <div key={l}>
                  <p className="font-display font-extrabold text-2xl">{n}</p>
                  <p className="text-[11px] text-slate-400 font-medium">{l}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="md:col-span-5 relative reveal" style={{ animationDelay: "0.15s" }}>
            <div className="rounded-3xl overflow-hidden shadow-2xl shadow-slate-300 rotate-1">
              <img src={HERO_IMG} alt="Luxury Indian homestay villa with pool" className="w-full h-[420px] object-cover" />
            </div>
            <div className="absolute -bottom-8 -left-10 hidden sm:block bg-slate-900 rounded-2xl p-4 shadow-2xl w-64 -rotate-1">
              <p className="text-[10px] font-semibold uppercase tracking-[0.15em] text-slate-400 mb-2.5">Casa Baga, Goa · this month</p>
              <div className="flex items-end justify-between">
                <p className="font-mono text-xl text-white">₹4,82,000</p>
                <span className="text-[11px] font-semibold text-emerald-400 flex items-center gap-1"><TrendingUp size={11} /> +18%</span>
              </div>
              <div className="h-1.5 bg-slate-700 rounded-full mt-2.5 overflow-hidden"><div className="h-full rounded-full bg-brand" style={{ width: "82%" }} /></div>
              <p className="text-[10px] text-slate-500 mt-2">82% occupancy · every number traceable</p>
            </div>
          </div>
        </div>
      </section>

      {/* PRODUCT SHOWCASE BENTO */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Inside the product</p>
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight mt-2 max-w-xl">Setup that feels like drawing, not data entry.</h2>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6 mt-10">
          <div className="md:col-span-5 bg-slate-50 border border-slate-200 rounded-3xl p-7 hover:shadow-xl hover:shadow-slate-200/70 transition-shadow">
            <span className="text-[11px] font-bold uppercase tracking-wider brand-pink">The connection canvas</span>
            <h3 className="font-display font-bold text-lg mt-2">AI draws the threads. You tap to confirm.</h3>
            <div className="bg-white rounded-2xl border border-slate-200 mt-4 p-4"><MiniThread /></div>
            <p className="text-sm text-slate-500 mt-3 leading-relaxed">Dashed = AI proposal, solid = your confirmed link. Same gesture for unit blocking, listing mapping and every OTA you connect.</p>
          </div>
          <div className="md:col-span-4 bg-slate-900 rounded-3xl p-7 text-white hover:shadow-xl transition-shadow">
            <span className="text-[11px] font-bold uppercase tracking-wider text-emerald-400">Pay per property</span>
            <h3 className="font-display font-bold text-lg mt-2">Activated the second payment clears.</h3>
            <div className="bg-[#131B2A] rounded-2xl p-4 mt-4 font-mono text-sm">
              <div className="flex justify-between text-slate-400 text-xs"><span>3 units × ₹475</span><span>₹1,425</span></div>
              <div className="flex justify-between text-slate-400 text-xs mt-1.5"><span>GST 18%</span><span>₹256.50</span></div>
              <div className="flex justify-between mt-3 pt-3 border-t border-slate-700 font-semibold"><span>Total</span><span className="brand-pink">₹1,681.50</span></div>
              <div className="flex items-center gap-1.5 mt-3 text-emerald-400 text-xs"><BadgeCheck size={13} /> PMS Active — instantly</div>
            </div>
            <p className="text-xs text-slate-400 mt-3 leading-relaxed">UPI with optional auto-debit. No waiting for the rest of your portfolio.</p>
          </div>
          <div className="md:col-span-3 bg-[#EEF2FF] border border-[#C7D2FE] rounded-3xl p-7 hover:shadow-xl hover:shadow-pink-100 transition-shadow">
            <span className="text-[11px] font-bold uppercase tracking-wider brand-pink">Bulk channel connect</span>
            <h3 className="font-display font-bold text-lg mt-2">Paste every ID at once.</h3>
            <div className="bg-white rounded-2xl border border-slate-200 mt-4 p-3 font-mono text-[11px] text-slate-500 space-y-1.5">
              <p className="flex items-center gap-1.5"><Check size={11} className="text-emerald-500" /> BDC-88213</p>
              <p className="flex items-center gap-1.5"><Check size={11} className="text-emerald-500" /> BDC-88214</p>
              <p className="flex items-center gap-1.5"><Check size={11} className="text-emerald-500" /> ABNB-55201</p>
              <p className="flex items-center gap-1.5 text-red-400">✕ XX-104 · flagged inline</p>
            </div>
            <p className="text-xs text-slate-500 mt-3 leading-relaxed">One pull fetches them all. Failures never block the rest.</p>
          </div>
        </div>
      </section>

      {/* P&L SIMULATOR */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">What commissions quietly cost you</h2>
            <p className="text-slate-600 mt-4 leading-relaxed">Big OTAs take 18–22% of every booking. Atiti charges a flat ₹475/unit/month — and 0% host commission on Atiti bookings. Slide to see your own numbers.</p>
            <div className="mt-8 space-y-6">
              <div>
                <label className="text-xs font-semibold text-slate-500">Units across your properties: <span className="brand-pink font-bold">{unitsN}</span></label>
                <input data-testid="pnl-unit-slider" type="range" min="1" max="40" value={unitsN} onChange={(e) => setUnitsN(+e.target.value)} className="w-full mt-2" />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-500">Average nightly rate: <span className="brand-pink font-bold">₹{adr.toLocaleString("en-IN")}</span></label>
                <input data-testid="pnl-adr-slider" type="range" min="1000" max="15000" step="250" value={adr} onChange={(e) => setAdr(+e.target.value)} className="w-full mt-2" />
              </div>
            </div>
          </div>
          <div className="bg-white rounded-3xl border border-slate-200 p-8 shadow-xl shadow-slate-200/60">
            <div className="space-y-5">
              <div>
                <div className="flex justify-between text-sm"><span className="text-slate-500">Annual OTA commission (18%)</span><span className="font-mono font-semibold text-red-500">−{fmt(otaCommission)}</span></div>
                <div className="h-3 bg-slate-100 rounded-full mt-2 overflow-hidden"><div className="h-full bg-red-400 rounded-full transition-all" style={{ width: "100%" }} /></div>
              </div>
              <div>
                <div className="flex justify-between text-sm"><span className="text-slate-500">Atiti PMS, all-in (₹475 × {unitsN} × 12)</span><span className="font-mono font-semibold text-slate-700">−{fmt(atitiCost)}</span></div>
                <div className="h-3 bg-slate-100 rounded-full mt-2 overflow-hidden"><div className="h-full bg-brand rounded-full transition-all" style={{ width: `${Math.min(100, (atitiCost / otaCommission) * 100)}%` }} /></div>
              </div>
              <div className="border-t border-slate-100 pt-5">
                <p className="text-xs font-semibold uppercase tracking-wider text-slate-400">Profit you keep every year</p>
                <p data-testid="pnl-savings-number" className="font-display font-extrabold text-4xl text-emerald-500 mt-1">{fmt(Math.max(saved, 0))}</p>
                <p className="text-[11px] text-slate-400 mt-2">Assumes 60% occupancy. Derivation: (units × ADR × 365 × 60%) × 18% − (units × ₹475 × 12).</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* STEPS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
        <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight max-w-lg">From listings to 150+ channels, in four taps-and-threads.</h2>
        <div className="mt-10 bg-slate-900 rounded-3xl p-8 grid grid-cols-1 md:grid-cols-12 gap-6 items-center">
          <div className="md:col-span-8">
            <span className="text-[11px] font-bold uppercase tracking-wider brand-pink">Step 1 — before anything else</span>
            <h3 className="font-display font-bold text-xl text-white mt-2">Create your listings on the Atiti app first.</h3>
            <p className="text-sm text-slate-400 mt-2 leading-relaxed">Every PMS journey starts with your listings living on Atiti. Download the app, list your spaces — and when you activate PMS, everything is pulled in automatically.</p>
          </div>
          <div className="md:col-span-4 flex flex-row md:justify-end gap-3">
            <a data-testid="marketing-playstore-badge" href="https://play.google.com/store/apps/details?id=com.atititi.app" target="_blank" rel="noopener noreferrer">
              <img src="https://nammayatri.in/logos/playStore.svg" alt="Get it on Google Play" className="h-11 hover:scale-[1.04] transition-transform" />
            </a>
            <a data-testid="marketing-appstore-badge" href="https://apps.apple.com/app/id6772948860" target="_blank" rel="noopener noreferrer">
              <img src="https://nammayatri.in/logos/appStore.svg" alt="Download on the App Store" className="h-11 hover:scale-[1.04] transition-transform" />
            </a>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-8">
          {STEPS.map(({ icon: Icon, t, d }, i) => (
            <div key={t} className="bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-lg hover:shadow-slate-200/70 hover:-translate-y-1 transition-all">
              <div className="flex items-center justify-between">
                <div className="w-10 h-10 rounded-xl bg-[#EEF2FF] flex items-center justify-center"><Icon size={18} className="brand-pink" /></div>
                <span className="font-display font-extrabold text-3xl text-slate-100">0{i + 2}</span>
              </div>
              <h3 className="font-display font-bold mt-4">{t}</h3>
              <p className="text-sm text-slate-500 mt-2 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CHANNEL SHOWCASE */}
      <section className="bg-slate-900 py-16 sm:py-24 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center mb-12">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">One subscription. Everywhere.</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-white mt-3"><span className="brand-pink">150+ OTA connections</span> worldwide, with Atiti</h2>
        </div>
        <div className="space-y-8">
          <div className="marquee-track">{[...CHANNELS_A, ...CHANNELS_A].map((c, i) => <span key={i} className="wordmark" style={{ color: "#94A3B8" }}>{c}</span>)}</div>
          <div className="marquee-track reverse">{[...CHANNELS_B, ...CHANNELS_B].map((c, i) => <span key={i} className="wordmark" style={{ color: "#64748B" }}>{c}</span>)}</div>
        </div>
        <p className="text-center text-[11px] text-slate-600 mt-10">150+ channel distribution · channel roster subject to final confirmation</p>
      </section>

      {/* PRICING */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
        <div>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">One price. Everything included.</h2>
          <p className="text-slate-600 mt-4 leading-relaxed">No PMS tier, no AI add-on, no channel fees. The same ₹475 you already know from the Atiti OTA covers the whole stack.</p>
          <ul className="mt-6 space-y-3">
            {["Atiti OTA presence, host & guest accounts", "Full property management, property-wise P&L", "AI setup, mapping & advisory agents", "150+ channel distribution built-in", "2% guest platform fee only — 0% host commission"].map((f) => (
              <li key={f} className="flex items-center gap-3 text-sm text-slate-700"><ShieldCheck size={16} className="brand-pink shrink-0" /> {f}</li>
            ))}
          </ul>
        </div>
        <div className="bg-brand rounded-3xl p-10 text-white text-center shadow-2xl shadow-pink-200">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/70">Per unit, per month</p>
          <p className="font-display font-extrabold text-6xl mt-3">₹475</p>
          <p className="text-sm text-white/80 mt-2">~$5 · unlimited listings · billing starts on first booking or PMS activation — whichever comes first</p>
          <button data-testid="pricing-cta-button" onClick={cta} className="bg-white brand-pink rounded-full px-8 py-3.5 text-sm font-bold mt-8 hover:scale-[1.02] transition-transform">
            {token ? "Open my PMS" : "Activate PMS now"}
          </button>
        </div>
      </section>

      {/* CALENDLY */}
      <section id="demo" className="bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight">Not sure yet? Watch it live.</h2>
            <p className="text-slate-600 mt-4 leading-relaxed">Pick a slot right here — no redirects. We'll walk your own listings through the setup flow on the call.</p>
            <div className="mt-6 flex items-center gap-3 text-sm text-slate-500"><CalendarCheck size={18} className="brand-pink" /> 30 minutes · Hindi / English · bring your listing IDs</div>
          </div>
          <div className="md:col-span-7 bg-white rounded-3xl border border-slate-200 overflow-hidden shadow-xl shadow-slate-200/60" style={{ minHeight: 480 }}>
            <iframe data-testid="calendly-embed" title="Book an Atiti PMS demo" src="https://calendly.com/atitihomestay?embed_domain=atiti.space&embed_type=Inline&hide_gdpr_banner=1&primary_color=e91e63"
              className="w-full" style={{ height: 700, border: 0 }} />
          </div>
        </div>
      </section>

      <footer className="bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <p className="text-center text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Trusted and Backed by the Best</p>
          <div className="flex items-center justify-center gap-12 mt-6">
            <img src="https://nammayatri.in/logos/beckn_logo.svg" alt="Beckn" className="h-7 opacity-70 hover:opacity-100 transition-opacity" />
            <img src="https://nammayatri.in/logos/juspayLogo.svg" alt="Juspay" className="h-7 opacity-70 hover:opacity-100 transition-opacity" />
            <img src="https://nammayatri.in/logos/ondcLogo.svg" alt="ONDC" className="h-7 opacity-70 hover:opacity-100 transition-opacity" />
          </div>
          <div className="flex items-center justify-between mt-12 pt-6 border-t border-slate-100">
            <Logo h={30} />
            <p className="text-xs text-slate-400 flex items-center gap-1.5"><Radio size={12} className="brand-pink" /> © 2026 Atiti · www.atiti.space · host-first, zero-commission</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
